import { useState, useRef, useEffect } from 'react';
import { Camera, Sparkles, Image as ImageIcon, Lock, Download, Share2, RefreshCw, Wand2, Smile, Settings as SettingsIcon, Headphones, VolumeX } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import MuziCoach from '../components/MuziCoach';
import FaceFilter from '../components/FaceFilter';
import DeviceTesterModal from '../components/DeviceTesterModal';
import KaraokeTracks from '../components/KaraokeTracks';
import LiveTransform, { LIVE_LOOKS } from '../components/LiveTransform';
import DuoStage, { DuoGallery } from '../components/DuoStage';
import { DUO_CHARACTERS } from '../lib/duoCharacters';
import { FILTERS, BACKGROUNDS, COACHES } from '../mock';
import { aiApi } from '../lib/api';
import { composeShareableImage, shareImage, captureFrameFromVideo } from '../lib/shareUtils';
import { useAuth } from '../contexts/AuthContext';

const FACE_EFFECTS = [
  { id: 'none', name: 'Aucun', emoji: '⚪' },
  { id: 'big_nose', name: 'Gros nez', emoji: '👃' },
  { id: 'big_lips', name: 'Grosses lèvres', emoji: '👄' },
  { id: 'big_head', name: 'Grosse tête', emoji: '🤯' },
  { id: 'tiny_head', name: 'Mini tête', emoji: '🤏' },
  { id: 'wavy', name: 'Fun house', emoji: '🌊' },
];

const AVATAR_STYLES = [
  { id: 'cartoon', name: 'Cartoon', emoji: '🎨' },
  { id: 'real', name: 'Néon', emoji: '✨' },
  { id: '3d', name: '3D', emoji: '🎭' },
  { id: 'manga', name: 'Manga', emoji: '🖋️' },
];

export default function Studio() {
  const { requireAuth, user } = useAuth();
  const videoRef = useRef(null);
  const audioMonitorRef = useRef(null); // {ctx, source, gain}
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);
  const [activeFilter, setActiveFilter] = useState(FILTERS[0].id);
  const [activeLook, setActiveLook] = useState('none');
  const [activeDuoId, setActiveDuoId] = useState(null);
  const [activeBg, setActiveBg] = useState(null);
  const [generatedAvatar, setGeneratedAvatar] = useState(null); // data URL
  const [activeCoach, setActiveCoach] = useState('c1');
  const [activeFaceEffect, setActiveFaceEffect] = useState('none');
  const [mode, setMode] = useState('real');
  const [recording, setRecording] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [voiceMonitor, setVoiceMonitor] = useState(false);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        // iOS Safari requires explicit play() after assigning srcObject
        try {
          await videoRef.current.play();
        } catch (e) {
          console.warn('[Studio] video.play() failed:', e?.message || e);
        }
        setStreaming(true);
      }
    } catch (e) {
      console.warn('[Studio] getUserMedia failed:', e?.message || e);
      toast.error('Accès caméra refusé', { description: 'Autorise caméra ET micro pour le Studio.' });
    }
  };

  // Toggle live voice monitoring (route mic → speakers).
  // Warning shown to user about headphones / feedback.
  const toggleVoiceMonitor = () => {
    if (!streamRef.current) {
      toast.error('Active d\'abord la caméra');
      return;
    }
    if (voiceMonitor) {
      // Stop monitoring
      try {
        audioMonitorRef.current?.source?.disconnect();
        audioMonitorRef.current?.gain?.disconnect();
        audioMonitorRef.current?.ctx?.close();
      } catch (err) {
        console.warn('[Studio] stop monitor failed:', err);
      }
      audioMonitorRef.current = null;
      setVoiceMonitor(false);
      toast.success('Monitoring désactivé');
      return;
    }
    // Start monitoring
    const audioTracks = streamRef.current.getAudioTracks();
    if (!audioTracks.length) {
      toast.error('Pas de micro détecté');
      return;
    }
    try {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctor();
      const source = ctx.createMediaStreamSource(new MediaStream(audioTracks));
      const gain = ctx.createGain();
      gain.gain.value = 0.9;
      source.connect(gain).connect(ctx.destination);
      audioMonitorRef.current = { ctx, source, gain };
      setVoiceMonitor(true);
      toast.info('🎧 Mets tes écouteurs ! Sinon larsen garanti.', { duration: 5000 });
    } catch (err) {
      console.warn('[Studio] AudioContext failed:', err);
      toast.error('Monitoring audio impossible sur cet appareil');
    }
  };

  // Cleanup audio on unmount
  useEffect(() => () => {
    try {
      audioMonitorRef.current?.ctx?.close();
    } catch (err) {
      console.warn('[Studio] AudioContext close failed:', err?.message || err);
    }
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch (err) {
      console.warn('[Studio] stream cleanup failed:', err?.message || err);
    }
  }, []);

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((t) => t.stop());
      setStreaming(false);
    }
  };

  useEffect(() => () => stopCamera(), []);

  const filterStyle = () => {
    // Prefer the LIVE LOOK CSS filter over the legacy color filter
    const look = LIVE_LOOKS.find((l) => l.id === activeLook);
    if (look && look.id !== 'none') return look.cssFilter;
    switch (activeFilter) {
      case 'f2': return 'hue-rotate(290deg) saturate(2) brightness(1.1)';
      case 'f3': return 'contrast(1.4) saturate(1.8) brightness(1.1)';
      case 'f4': return 'contrast(1.6) saturate(1.2)';
      case 'f5': return 'hue-rotate(20deg) saturate(1.8) brightness(1.15) contrast(1.1)';
      case 'f6': return 'hue-rotate(200deg) saturate(2.5) contrast(1.3)';
      case 'f7': return 'hue-rotate(160deg) saturate(2) brightness(1.2)';
      case 'f8': return 'sepia(0.7) contrast(1.1) brightness(0.95)';
      default: return 'none';
    }
  };

  const activeLookObj = LIVE_LOOKS.find((l) => l.id === activeLook);
  const liveTransformActive = !!activeLookObj?.mode && mode === 'real' && streaming;
  const activeDuoChar = DUO_CHARACTERS.find((c) => c.id === activeDuoId) || null;

  const generateAvatar = async (style) => {
    if (!requireAuth('login')) return;
    if (!streaming || !videoRef.current) {
      toast.error('Active d\'abord la caméra');
      return;
    }
    setAiGenerating(true);
    toast.info('🧠 Capture de ton visage...', { description: 'Place-toi bien au centre.' });

    // Capture a frame from the video into a canvas → base64
    let captured;
    try {
      const v = videoRef.current;
      const c = document.createElement('canvas');
      const size = Math.min(v.videoWidth || 480, v.videoHeight || 480, 768);
      c.width = size; c.height = size;
      const cx = c.getContext('2d');
      // Center-crop to square, mirror so it looks like the user expects
      const sx = (v.videoWidth - size) / 2;
      const sy = (v.videoHeight - size) / 2;
      cx.save();
      cx.scale(-1, 1);
      cx.translate(-size, 0);
      cx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
      cx.restore();
      captured = c.toDataURL('image/jpeg', 0.85);
    } catch (e) {
      setAiGenerating(false);
      toast.error('Capture caméra impossible');
      return;
    }

    try {
      toast.info('✨ L\'IA génère ton avatar... (~15s)');
      const r = await aiApi.generateAvatar({ image_base64: captured, style });
      setGeneratedAvatar(r.image_base64);
      setMode('avatar');
      toast.success('✨ Avatar généré !');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Génération échouée — réessaie';
      toast.error(msg);
    } finally {
      setAiGenerating(false);
    }
  };

  const bgObj = BACKGROUNDS.find((b) => b.id === activeBg);
  const faceEffectActive = activeFaceEffect !== 'none' && mode === 'real' && streaming;

  /**
   * Build a share-ready 9:16 image of the current Studio output.
   * - in avatar mode → use the generated avatar as source
   * - in real mode → capture the live video frame
   */
  const buildShareableBlob = async () => {
    const src = mode === 'avatar' && generatedAvatar
      ? generatedAvatar
      : captureFrameFromVideo(videoRef.current);
    if (!src) {
      toast.error('Active la caméra ou génère un avatar d\'abord');
      return null;
    }
    return await composeShareableImage(src, { username: user?.username || '' });
  };

  const handleShare = async () => {
    try {
      const blob = await buildShareableBlob();
      if (!blob) return;
      const result = await shareImage(blob, {
        filename: `muzibattle-${user?.username || 'me'}-${Date.now()}.png`,
        text: 'MUZI BATTLE — Chante. Joue. Deviens viral. → muzibattle.com',
      });
      if (result === 'shared') toast.success('Partagé ! 🚀');
      else if (result === 'downloaded') toast.success('Image téléchargée — ouvre Insta/TikTok et poste en story ! 🔥');
    } catch (e) {
      console.warn('[Studio] share failed:', e);
      toast.error('Partage impossible');
    }
  };

  const handleDownload = async () => {
    try {
      const blob = await buildShareableBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `muzibattle-${user?.username || 'me'}-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 100);
      toast.success('Image téléchargée ! 📥');
    } catch (e) {
      console.warn('[Studio] download failed:', e);
      toast.error('Téléchargement impossible');
    }
  };

  return (
    <div className="relative pt-24 pb-12 min-h-screen">
      <DeviceTesterModal open={showDeviceModal} onOpenChange={setShowDeviceModal} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <Badge className="bg-pink-500/15 text-pink-300 border-0 mb-2 px-3 py-1 rounded-full">
              <Camera size={12} className="mr-1" /> Studio
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-black">
              Crée ton <span className="text-gradient">personnage</span>
            </h1>
            <p className="text-zinc-400 mt-2">Caméra, filtres néon, déformations fun et avatars IA.</p>
          </div>
          <Button
            onClick={() => setShowDeviceModal(true)}
            variant="outline"
            className="bg-white/5 border-white/15 text-white hover:bg-white/10 rounded-xl h-10"
            data-testid="studio-test-devices-btn"
          >
            <SettingsIcon size={14} className="mr-1.5" /> Tester caméra & micro
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="relative aspect-video rounded-3xl overflow-hidden glass border border-white/10">
              {bgObj && (
                <img src={bgObj.img} alt={bgObj.name} className="absolute inset-0 w-full h-full object-cover opacity-60" />
              )}
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 via-purple-600/5 to-blue-500/10" />

              {mode === 'real' ? (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      filter: filterStyle(),
                      transform: 'scaleX(-1)',
                      // Keep video PLAYING — only hide visually when canvas overlay takes over.
                      // (iOS Safari pauses videos that become 'visibility:hidden'.)
                      opacity: (faceEffectActive || liveTransformActive) ? 0 : 1,
                    }}
                  />
                  <FaceFilter
                    videoRef={videoRef}
                    effect={activeFaceEffect}
                    colorFilter={filterStyle()}
                    active={faceEffectActive}
                  />
                  <LiveTransform
                    videoRef={videoRef}
                    look={activeLookObj}
                    active={liveTransformActive}
                  />
                  {activeDuoChar && streaming && (
                    <DuoStage
                      stream={streamRef.current}
                      character={activeDuoChar}
                      onClose={() => setActiveDuoId(null)}
                    />
                  )}
                </>
              ) : (
                generatedAvatar ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 gap-3 p-4" data-testid="avatar-result">
                    <img src={generatedAvatar} alt="Mon avatar IA" className="max-h-[80%] max-w-full object-contain bounce-soft rounded-2xl shadow-2xl shadow-pink-500/40" />
                    <Button
                      onClick={handleShare}
                      className="btn-neon h-11 px-5 rounded-xl text-white border-0 font-bold shadow-lg shadow-pink-500/40"
                      data-testid="avatar-share-btn"
                    >
                      <Share2 size={16} className="mr-1.5" /> Partager sur Insta/TikTok
                    </Button>
                  </div>
                ) : (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 bg-[#0A0A0F]/40 gap-3">
                    <div className="text-5xl">🎨</div>
                    <h3 className="text-xl font-bold text-white">Aucun avatar généré</h3>
                    <p className="text-zinc-400 text-sm">Active la caméra puis clique sur un style ci-contre →</p>
                    <Button onClick={() => setMode('real')} className="btn-neon h-10 px-4 rounded-xl text-white border-0 font-bold">
                      Retour caméra
                    </Button>
                  </div>
                )
              )}

              {!streaming && mode === 'real' && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6 bg-[#0A0A0F]/40">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center">
                    <Camera size={32} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white">Active ta caméra</h3>
                  <p className="text-zinc-400 max-w-md text-sm">Capture en temps réel avec filtres néon et déformations fun.</p>
                  <Button onClick={startCamera} className="btn-neon h-12 px-6 rounded-xl text-white border-0 font-bold">
                    <Camera size={18} className="mr-2" /> Activer la caméra
                  </Button>
                </div>
              )}

              {(streaming || mode === 'avatar') && (
                <div className="absolute bottom-4 left-4 right-4 md:right-auto">
                  <MuziCoach coachId={activeCoach} size="sm" />
                </div>
              )}

              {/* Voice monitor toggle — top-right overlay while camera streams */}
              {streaming && mode === 'real' && (
                <button
                  onClick={toggleVoiceMonitor}
                  className={`absolute top-3 right-3 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold backdrop-blur-md transition-colors ${
                    voiceMonitor
                      ? 'bg-pink-500/90 text-white shadow-lg shadow-pink-500/40'
                      : 'bg-black/50 text-white hover:bg-black/70 border border-white/15'
                  }`}
                  data-testid="voice-monitor-btn"
                  title={voiceMonitor ? 'Couper le retour audio' : "J'entends ma voix (casque conseillé)"}
                >
                  {voiceMonitor ? <Headphones size={12} /> : <VolumeX size={12} />}
                  {voiceMonitor ? "J'entends" : "Activer ma voix"}
                </button>
              )}

              {recording && (
                <div className="absolute top-4 left-4 flex items-center gap-2 bg-red-500/90 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold text-white">
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" /> REC
                </div>
              )}

              {(streaming || mode === 'avatar') && activeFaceEffect !== 'none' && (
                <div className="absolute top-4 right-4 glass-light px-3 py-1.5 rounded-full text-xs font-bold text-white flex items-center gap-1.5">
                  <Smile size={12} className="text-pink-400" /> {FACE_EFFECTS.find(e => e.id === activeFaceEffect)?.name}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 mt-4">
              <div className="flex gap-2">
                <button
                  onClick={() => { setMode('real'); if (!streaming) startCamera(); }}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${mode === 'real' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' : 'glass-light text-zinc-300 hover:text-white'}`}
                >
                  Réel revisité
                </button>
                <button
                  onClick={() => setMode('avatar')}
                  className={`px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${mode === 'avatar' ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white' : 'glass-light text-zinc-300 hover:text-white'}`}
                >
                  Avatar IA
                </button>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => { setRecording(!recording); toast.success(recording ? 'Enregistrement arrêté' : 'Enregistrement lancé'); }}
                  className={`h-10 px-4 rounded-xl font-bold ${recording ? 'bg-red-500 hover:bg-red-600' : 'btn-neon'} text-white border-0`}
                >
                  {recording ? 'Arrêter' : <><Camera size={16} className="mr-1.5" /> Enregistrer</>}
                </Button>
                <Button onClick={handleShare} className="h-10 px-4 rounded-xl glass-light hover:bg-white/10 text-white border-0 font-bold" data-testid="studio-share-btn">
                  <Share2 size={16} className="mr-1.5" /> Partager
                </Button>
                <Button onClick={handleDownload} className="h-10 w-10 rounded-xl glass-light hover:bg-white/10 text-white border-0 p-0" data-testid="studio-download-btn">
                  <Download size={16} />
                </Button>
              </div>
            </div>
          </div>

          <div className="space-y-5">
            <div className="glass rounded-2xl p-5 border-2 border-pink-500/30">
              <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                <Smile size={18} className="text-pink-400" /> Déformation
                <Badge className="bg-pink-500/20 text-pink-300 border-0 text-[10px] ml-auto">NEW</Badge>
              </h3>
              <p className="text-xs text-zinc-400 mb-3">Place ton visage au centre, sois drôle !</p>
              <div className="grid grid-cols-3 gap-2">
                {FACE_EFFECTS.map((fe) => (
                  <button
                    key={fe.id}
                    onClick={() => setActiveFaceEffect(fe.id)}
                    className={`aspect-square rounded-xl flex flex-col items-center justify-center text-xs font-semibold transition-all ${activeFaceEffect === fe.id ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white' : 'glass-light hover:bg-white/10 text-zinc-300'}`}
                  >
                    <div className="text-2xl">{fe.emoji}</div>
                    <div className="text-[10px] mt-1 px-1 leading-tight">{fe.name}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-3">
                <Wand2 size={18} className="text-pink-400" />
                <h3 className="font-bold text-white">Avatar IA</h3>
                <Badge className="bg-pink-500/20 text-pink-300 border-0 text-[10px] ml-auto">Nano Banana</Badge>
              </div>
              <p className="text-xs text-zinc-400 mb-3">L&apos;IA crée un avatar unique à partir de TON visage (caméra active requise).</p>
              <div className="grid grid-cols-2 gap-2">
                {AVATAR_STYLES.map((s) => (
                  <Button
                    key={s.id}
                    onClick={() => generateAvatar(s.id)}
                    disabled={aiGenerating || !streaming}
                    variant={s.id === 'cartoon' ? 'default' : 'outline'}
                    className={s.id === 'cartoon'
                      ? 'btn-neon text-white border-0 rounded-xl h-10 text-sm font-bold'
                      : 'bg-white/5 hover:bg-white/10 text-white border-white/15 rounded-xl h-10 text-sm font-bold'}
                    data-testid={`avatar-gen-${s.id}-btn`}
                  >
                    {aiGenerating ? <RefreshCw size={14} className="animate-spin" /> : <><span className="mr-1.5">{s.emoji}</span>{s.name}</>}
                  </Button>
                ))}
              </div>
              {!streaming && (
                <p className="text-[11px] text-yellow-400/80 mt-2">⚠️ Active d&apos;abord la caméra</p>
              )}
              {generatedAvatar && (
                <Button
                  onClick={() => { setGeneratedAvatar(null); setMode('real'); }}
                  variant="outline"
                  className="w-full mt-2 bg-white/5 text-zinc-300 border-white/10 rounded-xl h-9 text-xs"
                  data-testid="avatar-reset-btn"
                >
                  Effacer l&apos;avatar
                </Button>
              )}
            </div>

            <DuoGallery activeId={activeDuoId} onPick={setActiveDuoId} />

            <KaraokeTracks />

            <div className="glass rounded-2xl p-5 border-2 border-cyan-500/40">
              <h3 className="font-bold text-white mb-1 flex items-center gap-2">
                <Sparkles size={18} className="text-cyan-400" /> Transformation LIVE
                <Badge className="bg-cyan-500/20 text-cyan-300 border-0 text-[10px] ml-auto">HOT</Badge>
              </h3>
              <p className="text-xs text-zinc-400 mb-3">Effets temps réel sur ta vidéo — comic, sketch, pixel, néon...</p>
              <div className="grid grid-cols-3 gap-2">
                {LIVE_LOOKS.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setActiveLook(l.id)}
                    data-testid={`live-look-${l.id}`}
                    className={`aspect-square rounded-xl flex flex-col items-center justify-center text-[11px] font-semibold transition-all ${
                      activeLook === l.id
                        ? 'bg-gradient-to-br from-cyan-500 to-pink-500 text-white shadow-lg shadow-cyan-500/40 scale-105'
                        : 'glass-light hover:bg-white/10 text-zinc-300'
                    }`}
                  >
                    <div className="text-2xl mb-0.5">{l.emoji}</div>
                    {l.name}
                  </button>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <Sparkles size={18} className="text-purple-400" /> Filtres couleur
              </h3>
              <div className="grid grid-cols-4 gap-2">
                {FILTERS.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setActiveFilter(f.id)}
                    className={`aspect-square rounded-xl flex flex-col items-center justify-center text-xs font-semibold transition-all relative ${activeFilter === f.id ? 'bg-gradient-to-br from-pink-500 to-purple-600 text-white' : 'glass-light hover:bg-white/10 text-zinc-300'}`}
                  >
                    {f.premium && <Lock size={10} className="absolute top-1 right-1 text-yellow-400" />}
                    <div className="text-xl">{f.icon}</div>
                    <div className="text-[10px] mt-1">{f.name}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3 flex items-center gap-2">
                <ImageIcon size={18} className="text-blue-400" /> Arrière-plans
              </h3>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setActiveBg(null)}
                  className={`aspect-video rounded-xl border-2 ${!activeBg ? 'border-pink-500' : 'border-white/10'} bg-zinc-900 text-xs text-zinc-400 font-semibold`}
                >
                  Aucun
                </button>
                {BACKGROUNDS.map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setActiveBg(b.id)}
                    className={`aspect-video rounded-xl overflow-hidden border-2 relative ${activeBg === b.id ? 'border-pink-500' : 'border-transparent'}`}
                  >
                    <img src={b.img} alt={b.name} className="w-full h-full object-cover" />
                    {b.price > 0 && (
                      <div className="absolute top-1 right-1 bg-yellow-500 text-black text-[9px] font-black px-1.5 py-0.5 rounded-full">{b.price}€</div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div className="glass rounded-2xl p-5">
              <h3 className="font-bold text-white mb-3">Ton coach</h3>
              <div className="grid grid-cols-3 gap-2">
                {COACHES.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveCoach(c.id)}
                    className={`p-2 rounded-xl text-center transition-all ${activeCoach === c.id ? 'bg-gradient-to-br from-pink-500 to-purple-600' : 'glass-light hover:bg-white/10'}`}
                  >
                    <div className="text-2xl">{c.emoji}</div>
                    <div className="text-[10px] font-bold text-white mt-0.5">{c.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
